import {
    type FormElementFile,
    FormElementTypeId,
    WorksheetAssetImageType,
    WorksheetAssetType,
} from '@packages/clients';
import { cloneDeep } from 'lodash';

import type {
    WorksheetFormAssetEntity,
    WorksheetFormElementEntity,
    WorksheetFormElementFileEntity,
} from '../../../worksheets/forms/entities';
import type { CreateAnswerAssetBody } from '../operations/types';

/**
 * Represents a single worksheet element's answer state, including selected options,
 * optional file attachment, and grid cell answers. Used by {@link prepareElementAnswers}
 * to build the initial answer map from worksheet form elements.
 */
export type WorksheetElementAnswers = {
    /** The file attachment for this element, if any. */
    asset?: {
        /** The answer file metadata. */
        answerFile: FormElementFile;
    };

    /** The element this answer belongs to. */
    elementId: string;

    /** The display order of the element. */
    elementOrder?: string;

    /** The selected option values for this element. */
    selectedOptions: {
        /** The free-text answer for this option. */
        answerText: string;

        /** The option identifier. */
        optionId: string;

        /** The option display label. */
        optionLabel: string;
    }[];

    /** The server-assigned source ID linking this answer to submission data. */
    sourceId?: string;

    /** Grid cell answers for grid-type elements. */
    worksheetGridCellAnswers?: {
        /** The value entered in the grid cell. */
        value: string;

        /** The grid cell identifier. */
        worksheetElementGridId: string;
    }[];
};

/**
 * Build asset creation DTOs and a source-ID-to-file map from form element answers. Each
 * element answer that has a file attachment produces one {@link CreateAnswerAssetBody}
 * (either new or replacement). Callers use the returned `sourceIdFileMap` to upload the
 * actual files after creating the asset records.
 *
 * The `sourceId` for each answer is looked up from `answerSourceIds`, a map of element IDs
 * to server-assigned source IDs extracted from the PATCH response after worksheet answer
 * submission. Elements without a source ID are skipped.
 *
 * @param formElementAnswers - the current form element answers keyed by element ID
 * @param caseNumber - the Techline case number
 * @param techlineStatusCode - the current Techline case status code
 * @param answerSourceIds - map of element IDs to server-assigned source IDs from the submission response
 * @returns asset DTOs, element IDs, case status, and a sourceId-to-file map
 * @example
 * ```ts
 * const result = buildAnswerAssetBodies(answers, 'CASE-123', 'IN_REVIEW', { 'elem-1': 'src-1' });
 * // { assets: [...], elementIds: [...], sourceIdFileMap: { 'src-1': File }, techlineStatusCode: 'IN_REVIEW' }
 * ```
 */
export function buildAnswerAssetBodies(
    formElementAnswers: Record<string, WorksheetElementAnswers>,
    caseNumber: string,
    techlineStatusCode: string,
    answerSourceIds: Record<string, string>
) {
    const elementIds = Object.keys(formElementAnswers);
    const sourceIdFileMap: Record<string, File> = {};

    const assets = elementIds
        .map((elementId) => {
            const assetItem = formElementAnswers[elementId];

            // Look up the server-assigned sourceId by element ID
            const id = answerSourceIds[elementId];
            if (!id) {
                return null;
            }

            const file = assetItem.asset?.answerFile.file;
            const isUpdate = Boolean(assetItem.asset?.answerFile.assetId);

            if (file) {
                sourceIdFileMap[id] = file;
            }

            if (isUpdate) {
                return buildAnswerAssetReplacementBody(assetItem, caseNumber, id);
            }

            return buildAnswerAssetBody(assetItem, caseNumber, id);
        })
        .filter((assetItem) => assetItem !== null);

    return {
        assets,
        elementIds,
        sourceIdFileMap,
        techlineStatusCode,
    };
}

/**
 * Find the latest asset from a list of answer assets and return it as a
 * {@link WorksheetFormElementFileEntity}. Used to populate the initial answer state for file
 * elements when loading an existing worksheet submission.
 *
 * @param assets - the answer assets attached to a worksheet element
 * @returns a file entity representing the latest asset, or an empty shell if none exist
 * @example
 * ```ts
 * const file = findLatestAsset(element.answerAssets);
 * // { url: 'https://...', name: 'photo.jpg', assetId: '123' }
 * ```
 */
export function findLatestAsset(assets?: WorksheetFormAssetEntity[]): WorksheetFormElementFileEntity {
    if (!assets?.length) {
        return {
            url: undefined,
            name: undefined,
            caption: undefined,
            assetId: undefined,
        };
    }

    // Sort descending by creation timestamp to find the most recent
    const sorted = [...assets].sort(
        (a, b) => new Date(b.createdTimestamp).getTime() - new Date(a.createdTimestamp).getTime()
    );
    const asset = sorted[0];

    const media =
        asset.assetTypeId === WorksheetAssetType.FILE
            ? asset.media?.[0]
            : asset.media?.find((m) => m.assetImageTypeId === WorksheetAssetImageType.ORIGINAL);

    return {
        url: media?.url,
        name: asset.name,
        caption: asset.caption,
        assetId: asset.assetId,
        status: asset.assetProcessingStateId,
        overlay: {},
    };
}

/**
 * Extract all grid cells from a worksheet element, handling different grid
 * layouts (dynamic, pressure test, vehicle noise, valve clearance).
 *
 * @param element - the worksheet element containing the grid
 * @returns a flat array of grid cell objects
 * @example
 * ```ts
 * const cells = getGridCells(dynamicGridElement);
 * ```
 */
export function getGridCells(element: WorksheetFormElementEntity) {
    switch (element.worksheetElementTypeId) {
        case FormElementTypeId.DynamicGrid:
            return element.grid.flatMap((row) => row.columns ?? []);

        case FormElementTypeId.PressureTestGrid:
            return element.grid.flatMap((row) => (row.subRows ?? []).flatMap((subRow) => subRow.columns ?? []));

        case FormElementTypeId.VehicleNoiseGrid:
        case FormElementTypeId.ValveClearanceGrid:
            return element.grid.flatMap((row) => (row.columns ?? []).flatMap((column) => column.subColumns ?? []));

        default:
            return [];
    }
}

/**
 * Build the initial form element answer map from worksheet elements.
 * For each element that has existing answers (selected options, assets,
 * or grid cell answers), creates a WorksheetElementAnswers entry keyed
 * by element ID.
 *
 * @param elements - the worksheet form elements to extract answers from
 * @returns a record mapping element IDs to their answer state
 * @example
 * ```ts
 * const answers = prepareElementAnswers(worksheetData.elements);
 * // { 'elem-1': { elementId: 'elem-1', selectedOptions: [...], ... } }
 * ```
 */
export function prepareElementAnswers(elements: WorksheetFormElementEntity[]): Record<string, WorksheetElementAnswers> {
    let answersMap: Record<string, WorksheetElementAnswers> = {};
    const elementsClone = cloneDeep(elements);

    elementsClone?.forEach((element) => {
        if (
            element.selectedOptions?.length ||
            element.answerAssets?.length ||
            element.worksheetGridCellAnswers?.length
        ) {
            answersMap = {
                ...answersMap,
                [element.worksheetElementId]: {
                    elementId: element.worksheetElementId,
                    selectedOptions:
                        element.selectedOptions?.map((option) => ({
                            optionId: option.optionId,
                            optionLabel: option.optionLabel || '',
                            answerText: option.answerText || '',
                        })) || [],
                    asset: { answerFile: findLatestAsset(element.answerAssets) as FormElementFile },
                    worksheetGridCellAnswers:
                        element.worksheetGridCellAnswers?.map((cellAnswer) => ({
                            worksheetElementGridId: cellAnswer.worksheetElementGridId,
                            value: cellAnswer.value ?? '',
                        })) || [],
                },
            };
        }
    });

    return answersMap;
}

/**
 * Build a {@link CreateAnswerAssetBody} for a new file attachment on a worksheet answer.
 * Returns `null` when the answer has no file attached, allowing callers to filter
 * out no-op entries.
 *
 * @param answer - the form element answer containing the file attachment
 * @param caseNumber - the Techline case number the asset belongs to
 * @param sourceId - the server-assigned source ID linking the asset to its answer
 * @returns the asset creation body, or `null` if no file is attached
 * @example
 * ```ts
 * const body = buildAnswerAssetBody(answer, 'CASE-123', 'src-456');
 * // { assetDispositionId: 'TECHSHARE', assetTypeId: 'IMAGE', caseNumber: 'CASE-123', ... }
 * ```
 */
function buildAnswerAssetBody(
    answer: WorksheetElementAnswers,
    caseNumber: string,
    sourceId: string
): CreateAnswerAssetBody | null {
    const asset = answer.asset?.answerFile;
    if (!asset || !asset.file) {
        return null;
    }

    // Determine asset type based on MIME type
    const assetTypeId = asset.file.type.includes('image') ? WorksheetAssetType.IMAGE : WorksheetAssetType.FILE;

    return {
        assetDispositionId: 'TECHSHARE',
        assetTypeId,
        caseNumber,
        fileName: asset.file.name,
        hasOverlay: false,
        note: '',
        restricted: false,
        worksheetQuestionSubmissionSourceId: sourceId,
    };
}

/**
 * Build a {@link CreateAnswerAssetBody} that replaces an existing file attachment on a
 * worksheet answer. Returns `null` when the answer has no replacement file or no prior
 * asset to replace.
 *
 * @param answer - the form element answer containing the replacement file
 * @param caseNumber - the Techline case number the asset belongs to
 * @param sourceId - the server-assigned source ID linking the asset to its answer
 * @returns the asset creation body with `replacesAssetId`, or `null` if no replacement is needed
 * @example
 * ```ts
 * const body = buildAnswerAssetReplacementBody(answer, 'CASE-123', 'src-456');
 * // { ...baseFields, replacesAssetId: 'asset-789' }
 * ```
 */
function buildAnswerAssetReplacementBody(
    answer: WorksheetElementAnswers,
    caseNumber: string,
    sourceId: string
): CreateAnswerAssetBody | null {
    const asset = answer.asset?.answerFile;
    if (!asset || !asset.file || !asset.assetId) {
        return null;
    }

    return {
        assetDispositionId: 'TECHSHARE',
        assetTypeId: WorksheetAssetType.IMAGE,
        caseNumber,
        fileName: asset.file?.name || '',
        hasOverlay: false,
        name: asset.name || '',
        note: '',
        replacesAssetId: asset.assetId,
        restricted: false,
        worksheetQuestionSubmissionSourceId: sourceId,
    };
}
